import React, { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { setTimerRemaining, resetTimer } from '../store/appSlice';

const CountdownTimer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { timerRemaining, isPlaying, mode, backingTrack } = useAppSelector((state) => state.app);

  // Get duration from part settings in backing track mode
  const getPartDuration = useCallback((): number => {
    if (mode !== 'backingTrack' || !backingTrack.selectedTrackId || backingTrack.selectedPartIndex === null) {
      return 300; // default 5 minutes
    }
    const partSettings = backingTrack.partSettings[backingTrack.selectedTrackId]?.[backingTrack.selectedPartIndex];
    return partSettings?.duration || 300;
  }, [mode, backingTrack.selectedTrackId, backingTrack.selectedPartIndex, backingTrack.partSettings]);

  // Handle countdown when playing
  useEffect(() => {
    if (!isPlaying) {
      return;
    }

    const interval = setInterval(() => {
      dispatch(setTimerRemaining(Math.max(0, timerRemaining - 1)));
    }, 1000);

    return () => clearInterval(interval);
  }, [isPlaying, timerRemaining, dispatch]);

  // Reset timer when not playing or when part settings change
  useEffect(() => {
    if (!isPlaying) {
      const duration = getPartDuration();
      dispatch(resetTimer(duration));
    }
  }, [isPlaying, getPartDuration, dispatch]);

  // Don't show timer in metronome mode or if no part is selected in backing track mode
  if (mode === 'metronome' || (mode === 'backingTrack' && (backingTrack.selectedPartIndex === null || !backingTrack.selectedTrackId))) {
    return null;
  }

  // Visual display is rendered inside Player (next to the play/stop button).
  // This component is kept for its tick/reset side effects.
  return null;
};

export default CountdownTimer;
